import { dashboardPlatformKeyFromDescription } from "./utils/socialNetworkCatalog"

/**
 * Inbox stream settings (Settings → Inbox Settings → Streams).
 * Persisted per property until a dedicated API is available.
 */

export const INBOX_STREAM_PLATFORMS = [
  {
    id: "facebook",
    name: "Facebook",
    icon: "/assets/icons/social/facebook.svg",
  },
  {
    id: "twitter",
    name: "X",
    icon: "/assets/icons/social/x.png",
  },
  {
    id: "instagram",
    name: "Instagram",
    icon: "/assets/icons/social/instagram.svg",
  },
]

/** Fixed + rule stream descriptions shown under each row label. */
export const STREAM_HELP_TEXT = {
  facebook: {
    ratings: "Monitor Facebook page reviews and star ratings in the inbox.",
    posts: "Track posts published on your Facebook page and comments on those posts.",
    messages: "Monitor private messages sent to your Facebook page.",
  },
  twitter: {
    direct_messages: "Monitor direct messages (DMs) sent to your X account.",
    mentions: "Track posts and replies that @mention your X account.",
  },
  instagram: {
    story_mentions: "Track story mentions of your Instagram account.",
    direct_messages: "Monitor direct messages (DMs) sent to your Instagram account.",
    mentions: "Track posts and replies that @mention your Instagram account.",
    photo_tags: "Monitor posts where your account is tagged in photos.",
    comments: "Track comments on your Instagram posts.",
  },
}

export const TWITTER_RULE_HELP = {
  "Hotel Name": "Include posts and mentions that match this hotel or property name search.",
  Location: "Include posts that match this location search.",
  Sales: "Include posts and mentions that match this sales-related search.",
}

export const HASHTAG_STREAM_HELP =
  "Include posts and comments that contain this hashtag."

export const INSTAGRAM_LOCATION_HELP =
  "Include posts that match this location."

export const FIXED_STREAM_DEFAULTS = {
  facebook: [
    { id: "ratings", kind: "fixed", label: "Ratings", enabled: false },
    { id: "posts", kind: "fixed", label: "Posts/Comment", enabled: false },
    { id: "messages", kind: "fixed", label: "Messages", enabled: false },
  ],
  twitter: [
    { id: "direct_messages", kind: "fixed", label: "Direct Messages", enabled: false },
    { id: "mentions", kind: "fixed", label: "Mentions", enabled: false },
  ],
  instagram: [
    { id: "story_mentions", kind: "fixed", label: "Story Mentions", enabled: false },
    { id: "direct_messages", kind: "fixed", label: "Direct Messages", enabled: false },
    { id: "mentions", kind: "fixed", label: "Mentions", enabled: false },
    { id: "photo_tags", kind: "fixed", label: "Photo Tags", enabled: false },
    { id: "comments", kind: "fixed", label: "Comments", enabled: false },
  ],
}

export const TWITTER_ADDABLE_RULES = [{ streamType: "Hotel Name" }]

/** X/Twitter rule groups hidden from the Streams UI (API types may still exist server-side). */
export const TWITTER_HIDDEN_RULE_GROUP_KEYS = new Set([
  "twitter_rule:Location",
  "twitter_rule:Sales",
])

/** Instagram stream groups hidden from the Streams UI (API types may still exist server-side). */
export const INSTAGRAM_HIDDEN_GROUP_KEYS = new Set(["instagram_location"])

export const ADDABLE_STREAM_TYPES = {
  twitter: TWITTER_ADDABLE_RULES.map((rule) => ({
    kind: "twitter_rule",
    streamType: rule.streamType,
    label: rule.streamType,
  })),
  instagram: [{ kind: "hashtag", label: "Hashtag" }],
}

export function inboxStreamSettingsStorageKey(propertyId) {
  const pid =
    propertyId !== undefined && propertyId !== null && String(propertyId).trim() !== ""
      ? String(propertyId)
      : ""
  return pid ? `inbox_stream_settings_property_${pid}_v1` : `inbox_stream_settings_org_v1`
}

export function createStreamId() {
  return `stream_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
}

/** Normalize legacy saved rows to current shape. */
export function normalizeCustomStream(stream) {
  if (!stream || stream.kind === "fixed") return stream

  if (stream.kind === "hotel_name") {
    return {
      ...stream,
      kind: "twitter_rule",
      streamType: "Hotel Name",
      label: stream.label || "Hotel Name",
    }
  }

  if (stream.kind === "twitter_stream" || stream.kind === "twitter_rule") {
    let streamType = stream.streamType || stream.label || "Hotel Name"
    if (!stream.streamType && /^Sales(\s|$)/.test(String(stream.label || ""))) {
      streamType = "Sales"
    }
    return {
      ...stream,
      kind: "twitter_rule",
      streamType,
      label: streamType,
    }
  }

  return stream
}

export function createCustomStream(kind, options = {}) {
  if (kind === "twitter_rule") {
    const streamType = options.streamType || "Hotel Name"
    return {
      id: createStreamId(),
      kind: "twitter_rule",
      label: streamType,
      streamType,
      enabled: false,
      search: "",
    }
  }

  if (kind === "instagram_location") {
    return {
      id: createStreamId(),
      kind: "instagram_location",
      label: "Location",
      enabled: false,
      search: "",
    }
  }

  return {
    id: createStreamId(),
    kind,
    label: kind === "hashtag" ? "Hashtag" : "Stream",
    enabled: false,
    search: "",
  }
}

function mergePlatformStreams(platformId, savedStreams) {
  const fixedDefaults = FIXED_STREAM_DEFAULTS[platformId] || []
  const saved = Array.isArray(savedStreams) ? savedStreams : []
  const mergedFixed = fixedDefaults.map((def) => {
    const hit = saved.find((s) => s.kind === "fixed" && s.id === def.id)
    return hit ? { ...def, ...hit, kind: "fixed", label: def.label } : { ...def }
  })
  const custom = saved
    .filter((s) => s.kind && s.kind !== "fixed")
    .map((s) => normalizeCustomStream(s))
  return [...mergedFixed, ...custom]
}

export function createDefaultStreamSettings() {
  return {
    facebook: { streams: [...(FIXED_STREAM_DEFAULTS.facebook || [])] },
    twitter: { streams: [...(FIXED_STREAM_DEFAULTS.twitter || [])] },
    instagram: { streams: [...(FIXED_STREAM_DEFAULTS.instagram || [])] },
  }
}

export function loadInboxStreamSettings(propertyId) {
  try {
    const raw = localStorage.getItem(inboxStreamSettingsStorageKey(propertyId))
    if (!raw) return createDefaultStreamSettings()
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== "object") return createDefaultStreamSettings()
    return {
      facebook: { streams: mergePlatformStreams("facebook", parsed.facebook?.streams) },
      twitter: { streams: mergePlatformStreams("twitter", parsed.twitter?.streams) },
      instagram: { streams: mergePlatformStreams("instagram", parsed.instagram?.streams) },
    }
  } catch {
    return createDefaultStreamSettings()
  }
}

export function getConnectedStreamPlatformIds(accounts) {
  const connected = new Set()
  if (!Array.isArray(accounts)) return connected
  for (const acc of accounts) {
    const key = dashboardPlatformKeyFromDescription(acc?.socialNetworkType?.description)
    if (key === "facebook" || key === "twitter" || key === "instagram") {
      connected.add(key)
    }
  }
  return connected
}

export function isCustomStream(stream) {
  return stream?.kind && stream.kind !== "fixed"
}

/** Custom streams (hotel name, location, hashtag) need a search value before they can be enabled. */
export function getCommittedSearchValue(stream) {
  const s = normalizeCustomStream(stream)
  if (!isCustomStream(s)) return ""

  if (s.kind === "hashtag") {
    return normalizeHashtagValue(s.search)
  }

  return String(s.search || "")
}

export function searchDraftIsDirty(stream, draftValue) {
  const s = normalizeCustomStream(stream)
  if (!isCustomStream(s)) return false

  if (s.kind === "hashtag") {
    return normalizeHashtagValue(draftValue) !== normalizeHashtagValue(s.search)
  }

  return String(draftValue ?? "").trim() !== String(s.search ?? "").trim()
}

export function customStreamHasConfiguredSearch(stream) {
  const s = normalizeCustomStream(stream)
  if (!isCustomStream(s)) return true

  if (s.kind === "hashtag") {
    return normalizeHashtagValue(s.search).length > 0
  }

  if (s.kind === "twitter_rule" || s.kind === "instagram_location") {
    return String(s.search || "").trim().length > 0
  }

  return true
}

/** Turn off enabled flag on custom streams that have no search when persisting. */
export function sanitizeStreamSettingsForSave(settings) {
  if (!settings || typeof settings !== "object") return settings

  const next = {}
  for (const [platformId, platform] of Object.entries(settings)) {
    const streams = Array.isArray(platform?.streams) ? platform.streams : []
    next[platformId] = {
      streams: streams.map((raw) => {
        const stream = normalizeCustomStream(raw)
        if (isCustomStream(stream) && !customStreamHasConfiguredSearch(stream)) {
          return { ...stream, enabled: false }
        }
        return stream
      }),
    }
  }
  return next
}

export function normalizeHashtagValue(value) {
  return String(value || "").replace(/^#+/, "").trim()
}

export function getStreamHelp(platformId, stream) {
  if (!stream) return ""

  if (stream.kind === "fixed") {
    return STREAM_HELP_TEXT[platformId]?.[stream.id] || ""
  }

  if (stream.kind === "hashtag") return HASHTAG_STREAM_HELP

  if (stream.kind === "instagram_location") return INSTAGRAM_LOCATION_HELP

  if (stream.kind === "twitter_rule") {
    const type = stream.streamType || "Hotel Name"
    return TWITTER_RULE_HELP[type] || ""
  }

  return ""
}

export function labelForNewCustomStream(kind, _existingStreams, options = {}) {
  if (kind === "twitter_rule") {
    return labelForTwitterRule(options.streamType || "Hotel Name")
  }

  if (kind === "hashtag") return "Hashtag"
  if (kind === "instagram_location") return "Location"
  return "Stream"
}

export function labelForTwitterRule(streamType) {
  return streamType || "Hotel Name"
}

/** Group key for UI sections (e.g. all Hotel Name rows in one list). */
export function getCustomStreamGroupKey(stream) {
  const s = normalizeCustomStream(stream)
  if (s.kind === "hashtag") return "hashtag"
  if (s.kind === "instagram_location") return "instagram_location"
  if (s.kind === "twitter_rule") return `twitter_rule:${s.streamType || "Hotel Name"}`
  return `custom:${s.kind}`
}

export function getCustomStreamGroupTitle(groupKey) {
  if (groupKey === "hashtag") return "Hashtag"
  if (groupKey === "instagram_location") return "Location"
  if (groupKey.startsWith("twitter_rule:")) return groupKey.slice("twitter_rule:".length)
  return "Stream"
}

export function getAddableStreamGroupKey(item) {
  if (item?.kind === "twitter_rule") return `twitter_rule:${item.streamType || "Hotel Name"}`
  if (item?.kind === "hashtag") return "hashtag"
  if (item?.kind === "instagram_location") return "instagram_location"
  return `custom:${item?.kind || "stream"}`
}

/** Fixed streams plus every addable type for the platform (shown upfront). */
export function partitionPlatformStreams(streams, platformId) {
  const fixed = []
  const streamsByGroupKey = new Map()

  for (const raw of streams || []) {
    const stream = normalizeCustomStream(raw)
    if (!isCustomStream(stream)) {
      fixed.push(stream)
      continue
    }
    const key = getCustomStreamGroupKey(stream)
    if (!streamsByGroupKey.has(key)) streamsByGroupKey.set(key, [])
    streamsByGroupKey.get(key).push(stream)
  }

  const addable = ADDABLE_STREAM_TYPES[platformId] || []
  const groups = addable.map((item) => {
    const key = getAddableStreamGroupKey(item)
    const sample = createCustomStream(item.kind, { streamType: item.streamType })
    return {
      key,
      title: item.label,
      help: getStreamHelp(platformId, sample),
      addableItem: item,
      streams: streamsByGroupKey.get(key) || [],
    }
  })

  for (const [key, streamList] of streamsByGroupKey) {
    if (groups.some((g) => g.key === key)) continue
    if (platformId === "twitter" && TWITTER_HIDDEN_RULE_GROUP_KEYS.has(key)) continue
    if (platformId === "instagram" && INSTAGRAM_HIDDEN_GROUP_KEYS.has(key)) continue
    const sample = streamList[0]
    groups.push({
      key,
      title: getCustomStreamGroupTitle(key),
      help: getStreamHelp(platformId, sample),
      addableItem: null,
      streams: streamList,
    })
  }

  return { fixed, groups }
}

export function groupHasEmptySearchSlot(streams) {
  return (streams || []).some((s) => !customStreamHasConfiguredSearch(s))
}

/** Button label for adding another entry in a stream group (e.g. "Add hashtag"). */
export function labelForAddStreamGroup(groupTitle) {
  const name = String(groupTitle || "stream").trim().toLowerCase()
  return `Add ${name}`
}
